import React, { useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';

const PolitiqueConfidentialitePage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const goBack = () => {
    navigate('/');
  };

  return (
    <div className="mentions-legales-page">
      <div className="mentions-container">
        <button className="back-btn" onClick={goBack}>
          <ArrowLeft size={20} />
          Retour à l'accueil
        </button>

        <h1>Politique de Confidentialité</h1>
        <p className="last-update">Dernière mise à jour : Janvier 2025</p>

        <section>
          <h2>1. Introduction</h2>
          <p>
            La société NOTHING (ci-après "nous", "notre" ou "MonCoach") accorde une grande importance 
            à la protection de vos données personnelles. Cette politique de confidentialité vous informe 
            sur la manière dont nous collectons, utilisons et protégeons vos informations lorsque vous 
            utilisez notre service MonCoach.eu.
          </p>
        </section>

        <section>
          <h2>2. Responsable du traitement</h2>
          <div className="info-block">
            <p><strong>NOTHING</strong></p>
            <p>SASU au capital de 1 000,00 €</p>
            <p>58 rue de Monceau, 75008 Paris, France</p>
            <p>SIREN : 892 798 208</p>
            <p>Email : contact@moncoach.eu</p>
          </div>
        </section>

        <section>
          <h2>3. Données collectées</h2>
          <p>Nous collectons les données suivantes :</p>
          <div className="info-block">
            <p><strong>Données d'identification :</strong></p>
            <p>• Adresse email</p>
            <p>• Nom et prénom (facultatif)</p>
            <p><strong>Données de profil :</strong></p>
            <p>• Âge, sexe, poids, taille</p>
            <p>• Objectifs nutritionnels</p>
            <p>• Préférences alimentaires et allergies</p>
            <p><strong>Données d'utilisation :</strong></p>
            <p>• Historique des conversations avec Louis</p>
            <p>• Journal alimentaire</p>
            <p>• Évolution du poids</p>
            <p><strong>Données techniques :</strong></p>
            <p>• Adresse IP (anonymisée)</p>
            <p>• Type d'appareil et navigateur</p>
            <p>• Pages visitées</p>
          </div>
        </section>

        <section>
          <h2>4. Finalités du traitement</h2>
          <p>Vos données sont utilisées pour :</p>
          <div className="info-block">
            <p>• Fournir un accompagnement nutritionnel personnalisé</p>
            <p>• Générer des plans de repas adaptés à vos objectifs</p>
            <p>• Assurer le suivi de votre progression</p>
            <p>• Améliorer nos services et votre expérience utilisateur</p>
            <p>• Vous envoyer des communications relatives à votre compte (si consentement)</p>
            <p>• Assurer la sécurité et le bon fonctionnement du service</p>
          </div>
        </section>

        <section>
          <h2>5. Base légale du traitement</h2>
          <p>Le traitement de vos données repose sur :</p>
          <div className="info-block">
            <p>• <strong>L'exécution du contrat :</strong> pour vous fournir nos services</p>
            <p>• <strong>Votre consentement :</strong> pour les communications marketing</p>
            <p>• <strong>Notre intérêt légitime :</strong> pour améliorer nos services et assurer la sécurité</p>
          </div>
        </section>

        <section>
          <h2>6. Durée de conservation</h2>
          <p>Vos données sont conservées :</p>
          <div className="info-block">
            <p>• <strong>Données de compte :</strong> pendant toute la durée de votre inscription, puis 3 ans après la dernière activité</p>
            <p>• <strong>Données de conversation :</strong> 2 ans maximum</p>
            <p>• <strong>Données de facturation :</strong> 10 ans (obligation légale)</p>
            <p>• <strong>Cookies :</strong> 13 mois maximum</p>
          </div>
        </section>

        <section>
          <h2>7. Partage des données</h2>
          <p>
            Vos données personnelles ne sont <strong>jamais vendues</strong> à des tiers.
          </p>
          <p>Elles peuvent être partagées avec :</p>
          <div className="info-block">
            <p>• <strong>Nos sous-traitants techniques :</strong> hébergement (OVH), base de données (Supabase), paiement (Stripe)</p>
            <p>• <strong>Les autorités compétentes :</strong> en cas d'obligation légale</p>
          </div>
          <p>
            Tous nos sous-traitants sont soumis à des obligations strictes de confidentialité et de 
            protection des données conformes au RGPD.
          </p>
        </section>

        <section>
          <h2>8. Transfert hors UE</h2>
          <p>
            Certains de nos sous-traitants peuvent être situés en dehors de l'Union Européenne. 
            Dans ce cas, nous nous assurons que des garanties appropriées sont en place 
            (clauses contractuelles types, Privacy Shield, etc.).
          </p>
        </section>

        <section>
          <h2>9. Sécurité des données</h2>
          <p>Nous mettons en œuvre des mesures de sécurité appropriées :</p>
          <div className="info-block">
            <p>• Chiffrement des données en transit (HTTPS/TLS)</p>
            <p>• Chiffrement des mots de passe</p>
            <p>• Accès restreint aux données personnelles</p>
            <p>• Sauvegardes régulières</p>
            <p>• Surveillance et détection des intrusions</p>
          </div>
        </section>

        <section>
          <h2>10. Vos droits</h2>
          <p>Conformément au RGPD, vous disposez des droits suivants :</p>
          <div className="info-block">
            <p>• <strong>Droit d'accès :</strong> obtenir une copie de vos données</p>
            <p>• <strong>Droit de rectification :</strong> corriger vos données inexactes</p>
            <p>• <strong>Droit à l'effacement :</strong> demander la suppression de vos données</p>
            <p>• <strong>Droit à la portabilité :</strong> recevoir vos données dans un format structuré</p>
            <p>• <strong>Droit d'opposition :</strong> vous opposer au traitement de vos données</p>
            <p>• <strong>Droit à la limitation :</strong> limiter le traitement de vos données</p>
            <p>• <strong>Droit de retirer votre consentement :</strong> à tout moment</p>
          </div>
          <p>
            Pour exercer ces droits, contactez-nous à : <strong>contact@moncoach.eu</strong>
          </p>
          <p>
            Vous pouvez également introduire une réclamation auprès de la CNIL : www.cnil.fr
          </p>
        </section>

        <section>
          <h2>11. Cookies</h2>
          <p>Notre site utilise des cookies pour :</p>
          <div className="info-block">
            <p>• <strong>Cookies essentiels :</strong> fonctionnement du site, authentification</p>
            <p>• <strong>Cookies analytiques :</strong> mesure d'audience (anonymisés)</p>
          </div>
          <p>
            Vous pouvez gérer vos préférences de cookies dans les paramètres de votre navigateur.
          </p>
        </section>

        <section>
          <h2>12. Modifications</h2>
          <p>
            Nous pouvons mettre à jour cette politique de confidentialité à tout moment. 
            Nous vous informerons de tout changement significatif par email ou via une notification sur le site.
          </p>
        </section>

        <section>
          <h2>13. Contact</h2>
          <p>
            Pour toute question concernant cette politique de confidentialité ou vos données personnelles :
          </p>
          <div className="info-block">
            <p><strong>Email :</strong> contact@moncoach.eu</p>
            <p><strong>Adresse :</strong> NOTHING, 58 rue de Monceau, 75008 Paris, France</p>
          </div>
        </section>

        <div className="legal-nav">
          <p>Autres pages légales :</p>
          <div className="legal-links">
            <Link to="/mentions-legales">Mentions légales</Link>
            <Link to="/cgv">CGV / CGU</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PolitiqueConfidentialitePage;
